import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  Users, ArrowLeft, Plus, Trash2, Loader, Mail, Lock, User,
  ChevronDown, ChevronUp, Star
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { useLanguage } from "../hooks/useLanguage";

export default function ChurchFamiliesPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedFamily, setExpandedFamily] = useState(null);

  useEffect(() => {
    loadFamilies();
  }, []);

  const loadFamilies = async () => {
    try {
      const { data } = await axios.get(`${API}/church/families`);
      setFamilies(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFamily = async (familyId) => {
    if (!window.confirm(t('confirm_remove_family'))) return;
    
    try {
      await axios.delete(`${API}/church/families/${familyId}`);
      setFamilies(families.filter(f => f.id !== familyId));
    } catch (e) {
      alert(e.response?.data?.detail || t('could_not_remove'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <Loader size={48} className="animate-spin" color="#118AB2" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-10"
        style={{ background: "linear-gradient(135deg, #118AB2 0%, #06D6A0 100%)" }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/church")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4">
            <ArrowLeft size={16} /> {t('back')}
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-2xl p-3">
                <Users size={28} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('church_families')}
                </h1>
                <p className="text-white/70 text-sm font-medium">{families.length} families</p>
              </div>
            </div>
            <button onClick={() => setShowAddModal(true)}
              className="bg-white text-[#118AB2] font-black px-4 py-2 rounded-xl flex items-center gap-1"
              data-testid="add-family-btn">
              <Plus size={18} /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-5">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {families.length === 0 ? (
            <div className="p-8 text-center">
              <Users size={48} className="mx-auto mb-3 opacity-40" color="#118AB2" />
              <p className="text-gray-500 font-medium">No families yet</p>
              <p className="text-gray-400 text-sm">Add families or share your invite code</p>
            </div>
          ) : (
            <div className="divide-y">
              {families.map((family) => (
                <div key={family.id} className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center">
                      <User size={24} color="#118AB2" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-[#073B4C]">{family.name}</p>
                      <p className="text-xs text-gray-500">{family.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-400">
                        {family.children_count} kids
                      </span>
                      <button onClick={() => setExpandedFamily(expandedFamily === family.id ? null : family.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg">
                        {expandedFamily === family.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      <button onClick={() => handleRemoveFamily(family.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"
                        data-testid={`remove-family-${family.id}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded children */}
                  {expandedFamily === family.id && family.children?.length > 0 && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-2">
                      {family.children.map((child) => (
                        <div key={child.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                          <AvatarDisplay avatar={child.avatar} size={32} />
                          <div>
                            <p className="font-bold text-sm text-[#073B4C]">{child.name}</p>
                            <p className="text-xs text-gray-400">
                              {child.age_group === "little" ? "Little Light" : child.age_group === "big" ? "Explorer" : "Faith Builder"} · Age {child.age}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Family Modal */}
      {showAddModal && (
        <AddFamilyModal 
          onClose={() => setShowAddModal(false)} 
          onAdded={() => { setShowAddModal(false); loadFamilies(); }}
        />
      )}
    </div>
  );
}

function AddFamilyModal({ onClose, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/church/families/add`, form);
      onAdded();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not add family");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl w-full max-w-md p-6">
        <h2 className="text-xl font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
          Add Family
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Family Name</label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#118AB2] outline-none"
                placeholder="The Smith Family"
                required
                data-testid="family-name-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#118AB2] outline-none"
                placeholder="family@email.com"
                required
                data-testid="family-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-600 mb-1">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#118AB2] outline-none"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="family-password-input"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-xl">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold border-2 border-gray-200 text-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-3 rounded-xl font-bold text-white disabled:opacity-50"
              style={{ background: "#118AB2" }}
              data-testid="submit-add-family">
              {loading ? <Loader size={20} className="mx-auto animate-spin" /> : "Add Family"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
